import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@limitless-minds/ui-button'
import { Input } from '@limitless-minds/ui-input'
import { Paragraph } from '@limitless-minds/ui-text'
import CheckIcon from '@mui/icons-material/Check'
import { Stack, styled, useTheme } from '@mui/material'
import React from 'react'
import { Controller, FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import { useStore } from '@/store'
import HeaderWrapper from '@/ui/molecules/HeaderWrapper'
import { notNilNorEmpty } from '@/utils'

import { FIELDS, schemaStepTwo } from './schema'

const StyledDiv = styled(Stack)`
  justify-content: center;
  align-items: center;
  align-self: center;
  width: 100%;
  max-width: 25rem;
  height: 100%;
  gap: 2rem;
`

const StyledParagraph = styled(Paragraph)`
  text-decoration-line: line-through;
`

interface StepTwoData {
  email: string
  password: string
  repeatPassword: string
}

const StepTwo: React.FC = () => {
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { email, password, setEmail, setPassword, increaseStep } = useStore()

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<StepTwoData>({ resolver: yupResolver(schemaStepTwo) })

  const methods = useForm({
    resolver: yupResolver(schemaStepTwo),
    defaultValues: {
      [FIELDS.EMAIL]: email,
      [FIELDS.PASSWORD]: password,
    },
  })

  const currentPassword = useWatch({ name: FIELDS.PASSWORD, control })
  const repeatPassword = useWatch({ name: FIELDS.REPEAT_PASSWORD, control })

  const hasMinLength = currentPassword?.length >= 8
  const hasSymbol = /[!@#$%^&()_+\-=[\]{};':"\\|,.<>/?]/.test(currentPassword || '')
  const has12Chars = currentPassword?.length >= 12
  const passedRules = [hasMinLength, hasSymbol, has12Chars].filter(Boolean).length

  const isPasswordValid = passedRules >= 2 && currentPassword === repeatPassword

  const onSubmit: SubmitHandler<StepTwoData> = (data) => {
    const { email, password } = data
    setEmail(email)
    setPassword(password)
    increaseStep()
  }

  return (
    <StyledDiv>
      <HeaderWrapper
        title={
          <>
            Welcome to Neumi, <span style={{ fontWeight: 400 }}>powered by</span>{' '}
            <span style={{ whiteSpace: 'nowrap' }}>Limitless Minds</span>
          </>
        }
        description={
          <>
            You’re getting access to the Neumi platform.
            <br />
            <br />
            <strong>Plus:</strong> Our Newsletter, The Limitless Minds Playbook
            <br />
            Monthly mindset tools, performance hacks, podcast highlights, and insights from world-class coaches.
          </>
        }
      />
      <Stack gap='2rem' width='100%'>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <Stack gap='0.75rem'>
              <Controller
                name={FIELDS.EMAIL}
                control={control}
                defaultValue={email}
                render={({ field }) => (
                  <Input
                    label={'Email'}
                    {...field}
                    disabled={notNilNorEmpty(token)}
                    error={!!errors?.[FIELDS.EMAIL]}
                    errorMessage={errors?.[FIELDS.EMAIL]?.message}
                  />
                )}
              />
              <Controller
                name={FIELDS.PASSWORD}
                control={control}
                defaultValue={password}
                render={({ field }) => {
                  const currentPassword = field.value ?? ''
                  const getPasswordQuality = (): string => {
                    if (currentPassword.length === 0) return ''
                    if (currentPassword.length < 8) return 'Too short'
                    if (passedRules === 1) return 'Weak'
                    if (passedRules === 2) return 'Good'
                    if (passedRules >= 3) return 'Strong'
                    return ''
                  }

                  const getBarColor = (): string => {
                    // Three roles for four steps, and nothing is lost: the bar's WIDTH and the label
                    // beside it carry the fourth, so colour is the third channel rather than the only one.
                    //
                    // What each value owes is 3.0 against the TRACK it sits on, not against the card behind
                    // it - a state indicator under SC 1.4.11 is measured against what surrounds it. The
                    // three roles read 5.33 at worst across both modes, and `roles.ts` declares the pairs
                    // so a future move to any of them fails the build.
                    if (currentPassword.length === 0) return 'transparent'
                    if (currentPassword.length < 8) return theme.palette.role.error
                    if (passedRules === 1) return theme.palette.role.warning
                    if (passedRules >= 2) return theme.palette.role.success
                    return 'transparent'
                  }

                  const criteria = [
                    { text: 'Create a password with at least 8 characters', valid: hasMinLength },
                    { text: 'Use at least 1 symbol (/[!@#$%^&()_+-=[]{};:"\\|,.<>/?]/)', valid: hasSymbol },
                    { text: '(Optional) Use 12 characters or more', valid: has12Chars },
                  ]
                  return (
                    <>
                      <Input
                        label={'Password'}
                        {...field}
                        password
                        error={errors?.[FIELDS.PASSWORD] ? true : false}
                        errorMessage={errors?.[FIELDS.PASSWORD]?.message}
                      />
                      <div
                        style={{
                          height: 6,
                          // The track is a surface, so it follows the mode: a literal here is a fixed
                          // pale band across whatever card it lands on.
                          backgroundColor: theme.palette.role.outlineVariant,
                          borderRadius: 4,
                          overflow: 'hidden',
                          marginTop: 6,
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${Math.max((passedRules / 3) * 100, 5)}%`,
                            backgroundColor: getBarColor(),
                            transition: 'width 0.3s',
                          }}
                        />
                      </div>

                      <div style={{ marginTop: 4, fontSize: 14 }}>
                        Password quality: <strong>{getPasswordQuality()}</strong>
                      </div>

                      {criteria.map(({ text, valid }) =>
                        valid ? (
                          <Stack direction='row' alignItems='center' spacing={1} key={text}>
                            <CheckIcon />
                            <StyledParagraph key={text} color={theme.palette.role.onSurfaceMuted}>
                              {text}
                            </StyledParagraph>
                          </Stack>
                        ) : (
                          <Stack direction='row' alignItems='center' spacing={1} key={text}>
                            <CheckIcon sx={{ color: theme.palette.role.primary }} />
                            <Paragraph key={text} color={theme.palette.role.onSurface}>
                              {text}
                            </Paragraph>
                          </Stack>
                        )
                      )}
                    </>
                  )
                }}
              />
              <Controller
                name={FIELDS.REPEAT_PASSWORD}
                control={control}
                defaultValue={password}
                render={({ field }) => (
                  <Input
                    label={'Confirm Password'}
                    {...field}
                    password
                    error={errors?.[FIELDS.REPEAT_PASSWORD] ? true : false}
                    errorMessage={errors?.[FIELDS.REPEAT_PASSWORD]?.message}
                  />
                )}
              />
            </Stack>
            <Button type='submit' disabled={!isPasswordValid} width='100%'>
              Continue
            </Button>
          </form>
        </FormProvider>
      </Stack>
    </StyledDiv>
  )
}

export default StepTwo
